import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  MapPin,
  Phone,
  Globe,
} from "lucide-react";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function BranchManagement({ businessId, business }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Determine if this is user's own business or another business they're viewing
  const isOwnBusiness = business?.is_own_business;
  
  // The current business being viewed (passed from parent)
  const currentBusiness = business;

  useEffect(() => {
    if (businessId) {
      fetchBranches();
    }
  }, [businessId]);

  const fetchBranches = async () => {
    try {
      // Fetch branches for the current business being viewed
      const response = await fetch(`${API}/businesses/${businessId}/branches`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setBranches(data || []);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('dashboard:locations.locationManagement')}</h2>
          <p className="text-grey text-sm">
            {currentBusiness ? `${t('dashboard:locations.mainBusiness')}: ${currentBusiness.name}` : t('dashboard:locations.businessNotFound')}
          </p>
        </div>

        {/* Only show add branch button for own business */}
        {isOwnBusiness && currentBusiness && (
          <Button
            className="bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="add-branch-btn"
            onClick={() => navigate(`/register-business?parent=${businessId}`)}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard:locations.addNewLocation')}
          </Button>
        )}
      </div>

      {/* Main Business Card */}
      {currentBusiness && (
        <Card className="border-0 shadow-sm border-l-4 border-l-navy">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-navy/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-navy" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold text-lg text-navy">
                    {currentBusiness.name}
                  </h3>
                  <Badge className="bg-navy">{t('dashboard:locations.mainBusiness')}</Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-grey mt-1">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {currentBusiness.city}, {currentBusiness.district}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {currentBusiness.phone}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Branches List */}
      <div>
        <h3 className="font-medium text-navy mb-4">{t('dashboard:locations.locations')} ({branches.length})</h3>
        
        {branches.length > 0 ? (
          <div className="space-y-3">
            {branches.map((branch) => (
              <Card
                key={branch.business_id}
                className="border-0 shadow-sm hover:shadow-md transition-shadow"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-grey-100 rounded-xl flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-grey" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-navy">{branch.name}</h4>
                          <Badge variant="outline" className="text-xs">{t('dashboard:locations.location')}</Badge>
                          {branch.approval_status === "pending" && (
                            <Badge className="bg-yellow-100 text-yellow-800 text-xs">{t('dashboard:locations.pendingApproval')}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-grey">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {branch.city}, {branch.district}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-xl"
                        onClick={() => window.open(`/business/${branch.business_id}`, '_blank')}
                      >
                        <Globe className="w-4 h-4 mr-1" />
                        {t('dashboard:locations.view')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <Building2 className="w-12 h-12 text-grey mx-auto mb-3" />
              <p className="text-grey">{t('dashboard:locations.noLocationsAdded')}</p>
              {isOwnBusiness && (
                <p className="text-sm text-grey mt-1">
                  {t('dashboard:locations.addLocationHint')}
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Info Card - only show for own business */}
      {isOwnBusiness && (
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4">
            <h4 className="font-semibold text-blue-900 mb-2">{t('dashboard:locations.aboutLocationConnection')}</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• {t('dashboard:locations.locationInfo1')}</li>
              <li>• {t('dashboard:locations.locationInfo2')}</li>
              <li>• {t('dashboard:locations.locationInfo3')}</li>
              <li>• {t('dashboard:locations.locationInfo4')}</li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
